import React, { FC, useState, useEffect, useRef } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { useLocation, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import * as yup from 'yup'
import { getAuthClient, routes } from 'config'
import {
  constants,
  errorMsg,
  processErrorMsg,
  removeFocusFromField,
  storage,
  setCookie,
  deleteCookie,
  getCookieVal,
} from 'utils'
import { BaseLayout, Loader } from 'components'
import { LoginForm } from './components'
import { Grid } from '@mui/material'
import CommonCard from 'components/CommonCard/Card'

const Login: FC = () => {
  const formikRef: any = useRef()
  /**
   * using ref setValue of the input field if we need to set outside of the form
   */
  const setFormikInput = (key: string, value: string) => {
    formikRef.current.setFieldValue(key, value)
  }
  const { oktaAuth } = useOktaAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [loginArticle, setloginArticle] = useState<any | {}>({})
  const getLoginArticle = async () => {
    const data: any = sessionStorage.getItem('login_article')
    setloginArticle(JSON.parse(data))
  }
  /**
   * Clearing logging flag if set already in the session
   */
  useEffect(() => {
    sessionStorage.removeItem(
      constants.BROWSER_STORAGE_KEY.LOGGING_OPERATION_DONE,
    )
    getLoginArticle()
  }, [])

  useEffect(() => {
    const value = getCookieVal('brightLoginusername')
    /**
     * this settimeout is used because formik form need ms to load.
     * it is working fine.
     */
    setTimeout(() => {
      if (formikRef && formikRef.current) {
        setRememberMe(value ? true : false)
        setFormikInput('username', value ? decodeURI(value) : '')
      }
    }, 0)
  }, [formikRef])

  const { fromURI } = queryString.parse(location.search)
  let loginPassword = ''

  if (fromURI) {
    try {
      const redirectURL = new URL(fromURI.toString())
      sessionStorage.setItem(
        constants.BROWSER_STORAGE_KEY.REDIRECT_URL,
        `${redirectURL.origin}${redirectURL.pathname}`,
      )
    } catch (e) {
      processErrorMsg(
        e,
        'Login',
        'Setting redirection url in session storage. Used by /custom-log api',
      )
    }
  }

  const [transaction, setTransaction] = useState<any | {}>({})
  const [serviceMessage, setServiceMessage] = useState('')
  const [serviceError, setServiceError] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const state = location.state as {
    message: string
  }

  // time & date stamp for when the user's account will be unlocked
  const errorDate =
    window.history.state && window.history.state.state?.errorDate

  useEffect(() => {
    if (window.history.state?.state?.errorCode === 'E0000069') {
      console.log(window.history.state.state.errorCode)
      setServiceError(window.history.state.state.errorCode)
    } else if (window.history.state?.state?.errorCode === 'E0000011') {
      setServiceError(window.history.state.state.errorCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.history.state])

  const prevLengthHistory = JSON.parse(
    localStorage.getItem('previousHistoryLength') || '0',
  )

  useEffect(() => {
    // aka 'on reload'
    if (prevLengthHistory === window.history.length) {
      navigate('/login', { state: { errorDate: errorDate } })
    } else {
      localStorage.setItem(
        'previousHistoryLength',
        JSON.stringify(window.history.length),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevLengthHistory])

  const message = state?.message

  useEffect(() => {
    if (message) {
      setServiceMessage(message)
      /**
       * Clearing the location state
       */
      navigate(location.pathname, {
        state: '',
      })
    }
  }, [navigate, location.pathname, message])

  const processSuccess = (response: any) => {
    if (response.status === constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL) {
      const allFactorsNotSetup =
        !response.factors?.length ||
        response.factors.every((f: any) => f.status === 'NOT_SETUP')

      if (allFactorsNotSetup) {
        const stateToken = response.data?.stateToken || response.stateToken
        navigate(routes.forceEmailEnroll, {
          state: { stateToken },
        })
        return null
      }

      navigate(routes.enroll)
      return null
    } else if (
      response.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
    ) {
      navigate(routes.mfa, {
        state: { loginPassword },
      })
      return null
    } else if (
      response.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_EXPIRED ||
      response.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_WARN
    ) {
      // password is expiring soon (PASSWORD_WARN) or password already expired, let password-expired page handle both cases
      navigate(routes.passwordExpired, {
        state: { loginPassword },
      })
      return null
    } else if (
      response.status === constants.OKTA_TRANSACTION_STATUS.LOCKED_OUT
    ) {
      setServiceError('E0000069')
    }

    setShowLoader(false)
    setTransaction(response)
  }

  const processError = (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    setShowLoader(false)
    setServiceError(processErrorMsg(e, module, moduleDescription))
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    storage.setUser(values.username)

    loginPassword = values.password

    removeFocusFromField()

    setSubmitting(true)

    setShowLoader(true)
    setServiceMessage('')
    setServiceError('')

    localStorage.removeItem('okta-cache-storage')
    localStorage.removeItem('okta-shared-transaction-storage')

    const authClient = getAuthClient()
    let signInResponse: any
    if (rememberMe === false) {
      deleteCookie('brightLoginusername')
    }

    // Do Sign In
    try {
      signInResponse = await authClient.signIn({
        ...values,
        options: {
          warnBeforePasswordExpired: true,
        },
      })
    } catch (e) {
      processError(e, 'Login', 'Login Request')
      return
    }

    processSuccess(signInResponse)
    setSubmitting(false)
  }

  if (oktaAuth && transaction.sessionToken) {
    oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })
    return null
  }

  const initialValues: any = {
    username: '',
    password: '',
  }
  /***
   * @var rememberMeValue - its a bool value and it will setup on the basis of cookie
   *
   */

  const validationSchema: any = yup.object().shape({
    username: yup.string().required(errorMsg.requiredField('Login')).trim(),
    password: yup.string().required(errorMsg.requiredField('Password')),
  })
  const handleRemember = (): any => {
    if (rememberMe) {
      setRememberMe(false)
      deleteCookie('brightLoginusername')
    } else {
      setRememberMe(true)
      setCookie('brightLoginusername', formikRef.current.values.username, 90)
    }
  }
  return (
    <BaseLayout maxWidth={'74em'}>
      <Loader visible={showLoader} />
      <Grid container justifyContent="center">
        <Grid className="login-form" item xs={12} lg={5}>
          <LoginForm
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            serviceMessage={serviceMessage}
            serviceError={serviceError}
            rememberMeValue={rememberMe}
            handleRemember={handleRemember}
            formikRef={formikRef}
            errorDate={errorDate}
          />
        </Grid>
        {loginArticle &&
          loginArticle.pinnedArticles &&
          loginArticle.pinnedArticles[0] && (
            <Grid item xs={12} lg={6} className="card-container">
              <CommonCard
                title={
                  loginArticle &&
                  loginArticle.pinnedArticles &&
                  loginArticle.pinnedArticles[0] &&
                  loginArticle.pinnedArticles[0].title
                }
                subTitle={
                  loginArticle &&
                  loginArticle.pinnedArticles &&
                  loginArticle.pinnedArticles[0] &&
                  loginArticle.pinnedArticles[0].articleSummary
                }
                bottomAction={'READ MORE'}
                url={`${process.env.REACT_APP_DEFAULT_REDIRECTION}/article`}
                params={
                  loginArticle &&
                  loginArticle.pinnedArticles &&
                  loginArticle.pinnedArticles[0] &&
                  loginArticle.pinnedArticles[0].articleNewsId
                }
                elementID={'Read_More_home_card_id'}
              />
            </Grid>
          )}
      </Grid>
    </BaseLayout>
  )
}

export default Login
